import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { colors } from '../../../styles/tokens';
import { FORMAT_PRESETS, FormatPresetValue, getFormatHint } from '../../../constants/table';

export interface AddColumnModalProps {
  isOpen: boolean;
  isEnriching?: boolean;
  onClose: () => void;
  onAddColumn: (columnName: string, formatHint?: string) => Promise<void>;
}

/**
 * Modal for adding a new column with AI enrichment options
 */
export const AddColumnModal: React.FC<AddColumnModalProps> = ({
  isOpen,
  isEnriching = false,
  onClose,
  onAddColumn,
}) => {
  const [columnName, setColumnName] = useState('');
  const [formatPreset, setFormatPreset] = useState<FormatPresetValue>('none');
  const [customFormatInstructions, setCustomFormatInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setColumnName('');
      setFormatPreset('none');
      setCustomFormatInstructions('');
      setError(null);
    }
  }, [isOpen]);

  // Auto-suggest format preset based on column name
  useEffect(() => {
    const lowerValue = columnName.toLowerCase();
    if (formatPreset === 'none') {
      if (lowerValue.includes('date') || lowerValue.includes('founded') || lowerValue.includes('created')) {
        setFormatPreset('date');
      } else if (lowerValue.includes('tagline') || lowerValue.includes('slogan')) {
        setFormatPreset('tagline');
      } else if (lowerValue.includes('summary') || lowerValue.includes('description')) {
        setFormatPreset('summary');
      }
    }
  }, [columnName, formatPreset]);

  const handleSubmit = useCallback(async () => {
    if (!columnName.trim()) return;

    // Build format hint
    let formatHint = '';
    if (formatPreset !== 'none' && formatPreset !== 'custom') {
      formatHint = getFormatHint(formatPreset);
    } else if (formatPreset === 'custom' && customFormatInstructions.trim()) {
      formatHint = customFormatInstructions.trim();
    }

    try {
      await onAddColumn(columnName.trim(), formatHint);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add column');
    }
  }, [columnName, formatPreset, customFormatInstructions, onAddColumn, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && columnName.trim()) {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  }, [columnName, handleSubmit, onClose]);

  const isDisabled = isEnriching || !columnName.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="sm"
      showCloseButton={false}
    >
      <div className="-mt-4">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-lg font-bold text-neutral-900 mb-1.5">Add New Column</h3>
          <p className="text-13 text-neutral-500">
            Create a new column using AI to auto-enrich values.
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-neutral-200 -mx-6 mb-5" />

        {/* Column Name Field */}
        <div className="mb-4">
          <label className="block text-13 font-semibold text-neutral-700 mb-2">
            Column Name
          </label>
          <input
            type="text"
            value={columnName}
            onChange={(e) => setColumnName(e.target.value)}
            placeholder="Enter column name..."
            className="w-full h-10 px-3 text-sm border border-neutral-300 rounded-lg outline-none transition-all focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/12"
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </div>

        {/* Formatting & Style Section */}
        <div className="mb-4">
          <label className="block text-13 font-semibold text-neutral-700 mb-1">
            Formatting & Style
          </label>
          <p className="text-[11px] text-neutral-500 mb-2.5 leading-normal">
            Guide the AI on how to format or phrase the enrichment output.
          </p>

          {/* Format Preset Dropdown */}
          <select
            value={formatPreset}
            onChange={(e) => setFormatPreset(e.target.value as FormatPresetValue)}
            className="w-full h-[38px] px-3 text-13 border border-neutral-300 rounded-lg outline-none bg-white cursor-pointer transition-all focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/12"
            style={{ color: colors.neutral[900] }}
          >
            {FORMAT_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>

          {/* Custom Format Instructions Textarea */}
          {formatPreset === 'custom' && (
            <textarea
              value={customFormatInstructions}
              onChange={(e) => setCustomFormatInstructions(e.target.value)}
              placeholder="E.g., 'Respond in bullet points', 'Use only numbers', 'Format as a question'..."
              rows={3}
              className="mt-3 w-full p-3 text-13 border border-neutral-300 rounded-lg outline-none resize-y transition-all focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/12"
              style={{ color: colors.neutral[900], lineHeight: 1.5 }}
            />
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {/* Footer Divider */}
        <div className="h-px bg-neutral-200 -mx-6 mt-4 mb-3" />

        {/* Footer Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-13 font-medium text-neutral-500 bg-transparent border-none rounded-lg cursor-pointer transition-all hover:text-neutral-900 hover:bg-neutral-100"
          >
            Cancel
          </button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isDisabled}
            loading={isEnriching}
          >
            {isEnriching ? 'Creating...' : 'Add & Enrich'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

AddColumnModal.displayName = 'AddColumnModal';
